import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useMappedState } from "redux-react-hook";
import { Link, Redirect } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import {
  TextInput,
  Button,
  SelectOption,
  Form,
  FormGroup,
  ActionGroup,
  Alert,
  Spinner,
} from "@patternfly/react-core";
import {
  REQUESTED_CREATE_PRODUCT_GROUP,
  REQUESTED_UPDATE_PRODUCT_GROUP,
} from "../../redux/actions/productGroupAction";
import { REQUESTED_PRODUCTS } from "../../redux/actions/productAction";
import MultiTypeaheadSelectInput from "../general/MultipleSelect";
import DragableTable from "../general/DragableTable";

export default (props) => {
  const mapState = useCallback(
    (state) => ({
      productsMap: state.productState.productsMap,
      isFetchingProducts: state.productState.isFetchingProducts,
      isCreatingProductGroup: state.productGroupState.isCreatingProductGroup,
      isCreatedProductGroup: state.productGroupState.isCreatedProductGroup,
      isUpdatingProductGroup: state.productGroupState.isUpdatingProductGroup,
      isUpdatedProductGroup: state.productGroupState.isUpdatedProductGroup,
    }),
    []
  );

  const {
    productsMap,
    isFetchingProducts,
    isCreatingProductGroup,
    isCreatedProductGroup,
    isUpdatingProductGroup,
    isUpdatedProductGroup,
  } = useMappedState(mapState);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!productsMap) {
      dispatch({
        type: REQUESTED_PRODUCTS,
        payload: {},
      });
    }
  }, [dispatch, productsMap]);

  const productOptions =
    productsMap && !isFetchingProducts
      ? Object.keys(productsMap).map((key) => ({
          id: productsMap[key].id,
          value: key,
          disabled: false,
        }))
      : [];

  const productSelectOptions = productOptions.map((option, index) => (
    <SelectOption
      isDisabled={option.disabled}
      key={`product-${index}`}
      value={option.value}
    />
  ));

  const [productGroupName, setProductGroupName] = useState("");
  const [productGroupIndex, setProductGroupIndex] = useState("");
  const [productGroupAcronyms, setProductGroupAcronyms] = useState("");
  const [productGroupUpserted, setProductGroupUpserted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editProducts, setEditProducts] = useState();
  const [rows, setRows] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isEmpty(props.productGroup)) {
      setProductGroupName(
        !isEmpty(props.productGroup.name) ? props.productGroup.name : ""
      );
      setProductGroupIndex(
        !isEmpty(props.productGroup.index.toString())
          ? props.productGroup.index.toString()
          : ""
      );
      setProductGroupAcronyms(
        !isEmpty(props.productGroup.acronyms) ? props.productGroup.acronyms : ""
      );
      if (!isEmpty(props.productGroup.products)) {
        setSelectedProducts(props.productGroup.products.map((p) => p.name));
        setRows(
          props.productGroup.products.map((product, index) => {
            return {
              id: index + 1,
              name: product.name,
            };
          })
        );
      }
    }
  }, [props.productGroup]);

  function onSubmit(is_draft) {
    if (props.productGroup) {
      // console.log('---------updating-a-productGroup---------');
      let updatedata = {
        id: props.productGroup.id,
        productGroup: { is_draft },
      };
      if (!isEqual(productGroupName, props.productGroup.name)) {
        updatedata.productGroup.name = isEmpty(productGroupName)
          ? null
          : productGroupName;
      }
      if (!isEqual(productGroupAcronyms, props.productGroup.acronyms)) {
        updatedata.productGroup.acronyms = isEmpty(productGroupAcronyms)
          ? null
          : productGroupAcronyms;
      }
      if (!isEqual(parseInt(productGroupIndex), props.productGroup.index)) {
        updatedata.productGroup.index = isEmpty(productGroupIndex)
          ? null
          : parseInt(productGroupIndex);
      }

      if (!isEmpty(editProducts)) {
        updatedata.productGroup.productGroupMaps = editProducts
          .map((product, index) => ({
            id: product.id,
            name: product.name,
            product_index: product.product_index
              ? product.product_index
              : index + 1,
          }))
          .sort((a, b) => a > b);
      }
      // console.log(updatedata);
      if (productGroupName.trim() === "") {
        setErrorMsg("Product Group Name cannot be empty");
      } else if (editProducts && editProducts.length === 0) {
        setErrorMsg("Product Group must contain at least one product");
      } else {
        if (Object.keys(updatedata.productGroup).length > 0) {
          dispatch({
            type: REQUESTED_UPDATE_PRODUCT_GROUP,
            payload: { productGroup: updatedata },
          });
        }
      }
    } else {
      // console.log("---------creating-a-productGroup---------");
      let createdata = {
        name: isEmpty(productGroupName) ? null : productGroupName,
        index: isEmpty(productGroupIndex) ? null : productGroupIndex,
        acronyms: isEmpty(productGroupAcronyms) ? null : productGroupAcronyms,
        is_draft,
      };

      if (!isEmpty(editProducts)) {
        createdata = {
          ...createdata,
          productGroupMaps: editProducts.map((product, index) => ({
            id: product.id,
            name: product.name,
            product_index: product.product_index
              ? product.product_index
              : index + 1,
          })),
        };
      }
      // console.log(createdata);
      if (productGroupName.trim() === "") {
        setErrorMsg("Product Group Name cannot be empty");
      } else if (!editProducts || editProducts.length === 0) {
        setErrorMsg("Product Group must contain at least one product");
      } else {
        dispatch({
          type: REQUESTED_CREATE_PRODUCT_GROUP,
          payload: { productGroup: createdata },
        });
      }
    }
  }

  function renderButtons() {
    if (isCreatingProductGroup || isUpdatingProductGroup) {
      return <Spinner size="xl" />;
    }
    return (
      <>
        <Button
          variant="primary"
          onClick={() => onSubmit(true)}
        >{`Save as Draft`}</Button>
        <Button
          variant="secondary"
          onClick={() => onSubmit(false)}
        >{`Save & Publish`}</Button>
        <Link to="/product/group">
          <Button
            variant="tertiary"
            disabled={isCreatingProductGroup || isUpdatingProductGroup}
          >{`Cancel`}</Button>
        </Link>
      </>
    );
  }

  useEffect(() => {
    if (isCreatedProductGroup || isUpdatedProductGroup) {
      setProductGroupUpserted(true);
    }
  }, [isCreatedProductGroup, isUpdatedProductGroup]);

  function onSelectProduct(selected) {
    if (!isEqual(selected, selectedProducts)) {
      let newEditProducts = selected.map((e) => productsMap[e]);
      setEditProducts(newEditProducts);
      setRows(
        newEditProducts.map((product, index) => {
          return {
            id: index + 1,
            name: product.name,
          };
        })
      );
    }
  }

  const handleDrag = (rows) => {
    let newSortedProducts = rows.map((row, index) => {
      let matchedProduct = productsMap[row.name];
      return {
        id: matchedProduct.id,
        name: matchedProduct.name,
        product_index: index + 1,
      };
    });
    setEditProducts(newSortedProducts);
  };

  const renderErrorMsg = () => {
    if (errorMsg !== "") {
      return <Alert variant="danger" title={`${errorMsg}`} />;
    }
  };

  const columns = ["Index", "Product Name"];

  return (
    <>
      {productGroupUpserted ? <Redirect to="/product/group" /> : null}
      <Form>
        <FormGroup
          label="Name"
          isRequired
          fieldId="product-group-create-form-name"
        >
          <TextInput
            value={productGroupName}
            isRequired
            type="text"
            id="product-group-create-form-name"
            aria-describedby="product-group-create-form-name-helper"
            name="product-group-create-form-name"
            onChange={(value) => {
              setProductGroupName(value);
            }}
          />
        </FormGroup>
        <FormGroup
          label="Index of the group"
          fieldId="product-group-create-form-index"
        >
          <TextInput
            value={productGroupIndex}
            onChange={(value) => {
              setProductGroupIndex(value);
            }}
            type="text"
            id="product-group-create-form-index"
            name="product-group-create-form-index"
          />
        </FormGroup>
        <FormGroup
          label="Acronyms, Short Names"
          fieldId="product-group-create-form-acronyms"
        >
          <TextInput
            value={productGroupAcronyms}
            onChange={(value) => {
              setProductGroupAcronyms(value);
            }}
            type="text"
            id="product-group-create-form-acronyms"
            name="product-group-create-form-acronyms"
          />
        </FormGroup>
        <FormGroup
          label="Included Product"
          fieldId="product-group-create-form-member"
        >
          <MultiTypeaheadSelectInput
            key={JSON.stringify(selectedProducts)}
            titleId="member-multi-select"
            placeholder="Select Product"
            options={productSelectOptions}
            selected={selectedProducts}
            onSelect={(selected) => onSelectProduct(selected)}
          />
        </FormGroup>
        <FormGroup
          label="Product Order"
          fieldId="product-group-create-form-product-order"
        >
          <DragableTable
            columns={columns}
            rows={rows}
            handleDrag={handleDrag}
          />
        </FormGroup>
        {renderErrorMsg()}
        <ActionGroup>{renderButtons()}</ActionGroup>
      </Form>
    </>
  );
};
